import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import "./FormDropdown.scss";

export interface FormDropdownOption<T extends string = string> {
  value: T;
  label: string;
}

interface FormDropdownProps<T extends string> {
  value: T;
  options: FormDropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

interface MenuAnchor {
  openUp: boolean;
  maxHeight: number;
}

const getLabel = <T extends string>(
  options: FormDropdownOption<T>[],
  value: T,
  placeholder?: string
): string => {
  const match = options.find((option) => option.value === value);
  if (match) return match.label;
  return placeholder ?? "";
};

export const FormDropdown = <T extends string>({
  value,
  options,
  onChange,
  disabled = false,
  placeholder,
  className,
}: FormDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<MenuAnchor>({ openUp: false, maxHeight: 280 });
  const syncFrameRef = useRef(0);

  const initMenuAnchor = useCallback(() => {
    if (!rootRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const gap = 6;
    const maxMenuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const availableHeight = Math.min(maxMenuHeight, openUp ? spaceAbove : spaceBelow);

    anchorRef.current = {
      openUp,
      maxHeight: Math.max(availableHeight, 120),
    };
  }, []);

  const syncMenuPosition = useCallback(() => {
    if (!rootRef.current || !menuRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const gap = 6;
    const menu = menuRef.current;
    const { openUp, maxHeight } = anchorRef.current;

    menu.style.position = "fixed";
    menu.style.left = `${rect.left}px`;
    menu.style.width = `${rect.width}px`;
    menu.style.maxHeight = `${maxHeight}px`;
    menu.style.zIndex = "1400";

    if (openUp) {
      menu.style.top = "auto";
      menu.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      menu.style.bottom = "auto";
      menu.style.top = `${rect.bottom + gap}px`;
    }

    menu.style.visibility = "visible";
  }, []);

  const scheduleMenuPositionSync = useCallback(() => {
    if (syncFrameRef.current) return;

    syncFrameRef.current = window.requestAnimationFrame(() => {
      syncFrameRef.current = 0;
      syncMenuPosition();
    });
  }, [syncMenuPosition]);

  useLayoutEffect(() => {
    if (!open) return;

    initMenuAnchor();

    if (menuRef.current) {
      menuRef.current.style.visibility = "hidden";
    }

    syncMenuPosition();

    const onScrollOrResize = () => scheduleMenuPositionSync();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      if (syncFrameRef.current) {
        window.cancelAnimationFrame(syncFrameRef.current);
        syncFrameRef.current = 0;
      }
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, initMenuAnchor, syncMenuPosition, scheduleMenuPositionSync]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onEscape);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const displayLabel = getLabel(options, value, placeholder);
  const isPlaceholder = !options.some((option) => option.value === value);

  return (
    <div
      className={`create-request-modal-dropdown${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="create-request-modal-input create-request-modal-dropdown-trigger"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={isPlaceholder ? "create-request-modal-dropdown-placeholder" : undefined}>
          {displayLabel || placeholder}
        </span>
        <ChevronDown size={16} className="create-request-modal-dropdown-chevron" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="create-request-modal-dropdown-menu create-request-modal-dropdown-menu--portal"
            style={{ visibility: "hidden" }}
            role="listbox"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`create-request-modal-dropdown-option${value === option.value ? " is-active" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};
